import { useMemo, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import type { Demanda } from '@/models'
import { useDemandasQuery } from '@/views/demands-unified-old/hooks/queries/useDemandasQuery'
import { useDemandasAutorizacaoQuery } from '@/views/demands-unified-old/hooks/queries/useDemandasAutorizacaoQuery'
import { useEventListener } from '@/hooks/useEventBus'
import { useAutoRefresh } from '@/hooks/useAutoRefresh'

export interface DashboardStats {
  pending: number
  unfinished: number
  observable: number
  authorization: number
}

export const useHomeLogic = () => {
  const { user } = useAuth()
  const receivedQuery = useDemandasQuery('received')
  const watchingQuery = useDemandasQuery('watching')
  const toAssignQuery = useDemandasQuery('toAssign')

  const isGestorNoSetorLogado = useMemo((): boolean => {
    if (!user?.setor_logado_fk || !user?.setores) return false

    const setorLogadoComPivot = user.setores.find(
      (setor) => setor.id === user.setor_logado_fk
    )

    return setorLogadoComPivot?.pivot?.funcao === 'GESTOR'
  },[user?.setor_logado_fk, user?.setores])

  const autorizacaoQuery = useDemandasAutorizacaoQuery({
    enabled: isGestorNoSetorLogado // Só busca se for gestor no setor logado
  })

  const receivedDemands = useMemo(() => receivedQuery.data ?? [], [receivedQuery.data])
  const watchingDemands = useMemo(() => watchingQuery.data ?? [], [watchingQuery.data])
  const toAssignDemands = useMemo(() => toAssignQuery.data ?? [], [toAssignQuery.data])
  const autorizacaoDemands = useMemo(() => autorizacaoQuery.data ?? [], [autorizacaoQuery.data])

  const isLoading =
    !user ||
    receivedQuery.isLoading ||
    watchingQuery.isLoading ||
    toAssignQuery.isLoading ||
    (isGestorNoSetorLogado && autorizacaoQuery.isLoading)

  useEventListener(
    'sector-changed',
    () => {
      receivedQuery.forceRefresh()
      watchingQuery.forceRefresh()
      toAssignQuery.forceRefresh()
      if (isGestorNoSetorLogado) {
        autorizacaoQuery.forceRefresh()
      }
    },
    [receivedQuery.forceRefresh, watchingQuery.forceRefresh, toAssignQuery.forceRefresh, toAssignQuery.forceRefresh, autorizacaoQuery.forceRefresh, isGestorNoSetorLogado]
  )

  useEventListener(
    'cache-invalidated',
    ({ cacheKeys }) => {
      if (cacheKeys.some((key) =>
        ['received', 'watching', 'toAssign', 'all'].some((pattern) => key.includes(pattern))
      )) {
        receivedQuery.forceRefresh()
        watchingQuery.forceRefresh()
        toAssignQuery.forceRefresh()
        if (isGestorNoSetorLogado){
          autorizacaoQuery.forceRefresh()
        }
      }
    },
    [receivedQuery.forceRefresh, watchingQuery.forceRefresh, toAssignQuery.forceRefresh, toAssignQuery.forceRefresh, autorizacaoQuery.forceRefresh, isGestorNoSetorLogado]
  )

  // Auto-refresh a cada 30 segundos
  useAutoRefresh({
    interval: 30 * 1000,
    enabled: true,
    onRefresh: useCallback(async () => {
      await Promise.all([
        receivedQuery.forceRefresh(),
        watchingQuery.forceRefresh(),
        toAssignQuery.forceRefresh(),
        ...(isGestorNoSetorLogado ? [autorizacaoQuery.forceRefresh()] : []),
      ])
    }, [receivedQuery, watchingQuery, toAssignQuery, autorizacaoQuery, isGestorNoSetorLogado]),
    minInterval: 30 * 1000,
    maxInterval: 30 * 1000,
  })

  const stats: DashboardStats = useMemo(() => {
    if (!user) {
      return {
        pending: 0,
        unfinished: 0,
        observable: 0,
        authorization: 0,
      }
    }

    const pendingCount = receivedDemands.filter(
      (d) => d.status === 'RECEBIDA' || d.status === 'EMATRIBUICAO'
    ).length

    const unfinishedCount = receivedDemands.filter((d) =>
      ['ATRIBUIDA', 'EMPROGRESSO', 'AGUARDANDOAUTORIZACAO'].includes(d.status ?? '')
    ).length

    const observableCount = watchingDemands.length

    return {
      pending: pendingCount,
      unfinished: unfinishedCount,
      observable: observableCount,
      authorization: autorizacaoDemands.length,
    }
  }, [receivedDemands, watchingDemands, user, autorizacaoDemands])

  // Demandas não atribuídas do setor (incluindo as atrasadas)
  const getUnassignedDemands = useCallback((): Demanda[] => {
    if (!user?.setor_logado_fk) return []

    return toAssignDemands.filter((d) => {
      // Verifica se é do setor e está em atribuição
      const isFromSector = (d.status === 'EMATRIBUICAO' || !d.demandado_fk) &&
        (d.setor_fk === user.setor_logado_fk || d.setor?.id === user.setor_logado_fk)
      
      return isFromSector
    })
  }, [toAssignDemands, user?.setor_logado_fk])

  // Demandas solucionadas - status SOLUCIONADA
  const getSolvedDemands = useCallback((): Demanda[] => {
    return receivedDemands.filter((d) => d.status === 'SOLUCIONADA')
  }, [receivedDemands])

  // Demandas aguardando autorização do gestor
  const getAuthorizationDemands = useCallback((): Demanda[] => {
    return autorizacaoDemands
  }, [autorizacaoDemands])

  // Demandas pendentes do usuário - atribuídas ao usuário logado (incluindo as atrasadas)
  const getPendingDemands = useCallback((): Demanda[] => {
    if (!user) return []

    return receivedDemands.filter((d) => {
      // Verifica se o usuário é responsável pela demanda
      const isAssignedToUser = 
        d.demandado_fk === user.id || // Campo único
        d.demandados?.some(demandado => demandado.id === user.id) || // Array demandados
        d.atribuidas?.some(atribuida => atribuida.usuario_fk === user.id && atribuida.active) // Array atribuidas
      
      if (!isAssignedToUser) return false

      // Exclui demandas finalizadas ou solucionadas
      if (d.status === 'FINALIZADA' || d.status === 'SOLUCIONADA') return false

      return true
    })
  }, [receivedDemands, user])

  return {
    user,
    isLoading,
    stats,
    getUnassignedDemands,
    getSolvedDemands,
    getPendingDemands,
    getAuthorizationDemands,
    isGestorNoSetorLogado,
  }
}
